import { observer } from "mobx-react";
import { AcxGradientBorderContainer } from "../../../UI/AcxGradientBorderContainer";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Collapse, Grid, styled, Typography } from "@mui/material";
import { AcxGradientTypography } from "components/UI/AcxGradientTypography";
import {
    ArrowForwardRounded,
    Cached,
    ContentCopyRounded,
} from "@mui/icons-material";
import { useStore } from "utils/useStore";
import Markdown from "markdown-to-jsx";
import { AcxAgentService } from "services/AcxAgentService";
import { ApplicationFiltersStore } from "stores/ApplicationFilters/ApplicationFiltersStore";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import theme from "Theme/AppTheme";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import AcxChip from "components/UI/AcxChip";
import AcxButton from "components/UI/AcxButton";
import { AuthStore } from "stores/AuthStore";
import { useCurrentChatStore } from "components/KBo/Stores/useCurrentChat";
import ChartCardStore from "./ChartCardStore";
import OrgOptionsStore from "stores/OrgOptionsStore";
import { SpotlightSparkle } from "./Spotlight";
import { KboModelOption } from "../_DataEndpointConfig/Types";

const AcxIconButton = styled(AcxButton)(({ theme }) => ({
    color: theme.palette.gray[400],
    backgroundColor: "transparent",
}));

export const SignalsAnalyzeWithKboSpotlight = observer(
    ({ store }: { store: ChartCardStore }) => {
        const currentChatStore = useCurrentChatStore();
        const aiSummaryStore = store.aiSummaryStore;
        const applicationFiltersStore = useStore(ApplicationFiltersStore);
        const authStore = useStore(AuthStore);
        const optionsStore = useStore(OrgOptionsStore);

        // When the chart's config supplies a classifier loader, the card shows the "...for {model}"
        // selector and the chosen classifier (rather than the chart's single selectedIds.llmClassifier)
        // drives the analysis. How those classifiers are sourced is entirely owned by the chart config.
        const modelLoader = store.config.kboModelOptions;
        const llmClassifiers = optionsStore.llmClassifiers;

        const [selectedResult, setSelectedResult] = useState<string>("");
        const [isExpanded, setIsExpanded] = useState(true);
        const [canGoDeeper, setCanGoDeeper] = useState(true);
        const [classifierOptions, setClassifierOptions] = useState<
            KboModelOption[]
        >([]);
        const [selectedClassifierId, setSelectedClassifierId] = useState<
            string | undefined
        >(undefined);

        const activeClassifierId = modelLoader
            ? selectedClassifierId
            : (store.selectedIds.llmClassifier as string | undefined);

        // Classifier ids that actually rendered a bar on the current chart. Selections with no data are
        // hidden from the model selector entirely.
        const classifiersWithData = useMemo(
            () =>
                new Set(
                    (store.data ?? [])
                        .map((datum) => datum.x_identifier)
                        .filter((id): id is string => !!id),
                ),
            [store.data],
        );

        // The models offered in the selector: loaded group members that have data on the current chart.
        const selectableOptions = useMemo(
            () =>
                classifierOptions.filter((c) => classifiersWithData.has(c.id)),
            [classifierOptions, classifiersWithData],
        );

        // Loader mode: resolve the selectable classifiers (and their results) for the model selector.
        useEffect(() => {
            if (!modelLoader) return;
            let disposed = false;
            void modelLoader
                .loadOptions({
                    dataEndpoint: store.dataEndpoint,
                    classifierGroup: store.selectedIds.classifierGroup,
                    llmClassifiers,
                })
                .then((options) => {
                    if (disposed) return;
                    setClassifierOptions(options);
                });
            return () => {
                disposed = true;
            };
        }, [
            modelLoader,
            store.dataEndpoint,
            store.selectedIds.classifierGroup,
            llmClassifiers,
        ]);

        // Keep the analyzed model valid as options/data change: default to the first analyzable model
        // that has data (group is name-ordered), keeping the current selection when it still qualifies.
        // Lucene (RBC) members have no reasoning to analyze, so they are never auto-selected.
        useEffect(() => {
            if (!modelLoader) return;
            const eligible = selectableOptions.filter((o) => o.analyzable);
            setSelectedClassifierId((current) =>
                current && eligible.some((o) => o.id === current)
                    ? current
                    : eligible[0]?.id,
            );
        }, [modelLoader, selectableOptions]);

        // Reset to "All Results" whenever the analyzed classifier changes.
        useEffect(() => {
            setSelectedResult("");
        }, [activeClassifierId]);

        const refreshKboSummary = useCallback(
            (result?: string) => {
                if (!aiSummaryStore || !activeClassifierId) {
                    return new AbortController();
                }
                const service = new AcxAgentService();
                const abortController = new AbortController();

                store.captureAISummaryContextForCurrentPage();
                aiSummaryStore.refreshSummaryCustom(
                    store.id,
                    async () => {
                        const response = await service.summarizeModelResults(
                            applicationFiltersStore.toRequestObject(),
                            activeClassifierId,
                            result,
                            abortController.signal,
                        );
                        setCanGoDeeper(true);
                        return response.summary;
                    },
                    {
                        errorMessage:
                            "Sorry, there was an issue generating the analysis. Please refresh to try again.",
                        onError() {
                            setCanGoDeeper(false);
                        },
                    },
                );
                return abortController;
            },
            [
                store,
                applicationFiltersStore,
                aiSummaryStore,
                activeClassifierId,
            ],
        );

        useEffect(() => {
            const abortController = refreshKboSummary();

            return () => abortController.abort();
        }, [refreshKboSummary]);

        // Loader mode resolves the classifier from the loaded options; otherwise the chart's single
        // selected classifier.
        const activeClassifier = modelLoader
            ? classifierOptions.find((c) => c.id === activeClassifierId)
            : llmClassifiers.find((c) => c.id === activeClassifierId);

        if (!store.isDisplayingRenderableChartData) return null;

        // No analyzable classifier (e.g. a group with no published-with-results members) — render nothing.
        if (!activeClassifier) return null;

        function onGoDeeperClick() {
            currentChatStore?.analyzeModelResultsAction(
                activeClassifier!,
                selectedResult,
            );
            setCanGoDeeper(false);
        }

        if (!aiSummaryStore) return null;

        const resultOptions = activeClassifier.results
            .map((o) => ({ label: o, value: o }))
            .concat([{ label: "All Results", value: "" }]);

        const isLoading = aiSummaryStore.isSummaryLoading;
        const hasSummaryToShow = !isLoading && !!aiSummaryStore.summary;

        const isGoDeeperVisible =
            hasSummaryToShow && authStore.canUserView("Model Results");

        const headerComponent = (
            <Grid
                container
                item
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="nowrap"
            >
                <Grid
                    container
                    item
                    alignItems="center"
                    columnGap={1}
                    flexWrap="nowrap"
                    onClick={() => setIsExpanded((expanded) => !expanded)}
                    sx={{
                        "&:hover": {
                            cursor: "pointer",
                        },
                    }}
                >
                    <AcxGradientTypography
                        sx={(theme) => ({
                            color: theme.palette.secondary.dark,
                            textTransform: "uppercase",
                            letterSpacing: "2px",
                        })}
                    >
                        Analyze With Kbo
                    </AcxGradientTypography>
                    <SpotlightSparkle />
                    {aiSummaryStore.isOutdated && (
                        <AcxChip
                            size="small"
                            color="gray"
                            label="Current filters not reflected"
                        />
                    )}
                </Grid>
                <Grid
                    container
                    item
                    alignItems="center"
                    columnGap={1}
                    width="fit-content"
                    flexWrap="nowrap"
                >
                    {isExpanded && (
                        <React.Fragment>
                            <Grid item>
                                <AcxIconButton
                                    id="analyze-with-kbo-copy"
                                    fullWidth
                                    variant="text"
                                    startIcon={<ContentCopyRounded />}
                                    onClick={(e) => {
                                        aiSummaryStore.copySummaryToClipboard();
                                    }}
                                    disabled={!hasSummaryToShow}
                                >
                                    Copy
                                </AcxIconButton>
                            </Grid>
                            <Grid item>
                                <AcxIconButton
                                    id="analyze-with-kbo-refresh"
                                    fullWidth
                                    variant="text"
                                    startIcon={<Cached />}
                                    disabled={
                                        isLoading ||
                                        !aiSummaryStore.isRefreshable
                                    }
                                    onClick={(e) => {
                                        refreshKboSummary(selectedResult);
                                    }}
                                >
                                    Refresh
                                </AcxIconButton>
                            </Grid>
                        </React.Fragment>
                    )}
                </Grid>
            </Grid>
        );

        const inlineSelectLabelSx = (muiTheme: typeof theme) => ({
            color: muiTheme.palette.gray[600],
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 600,
        });

        // Shared styling for the inline, borderless dropdowns rendered within the "Analyze ... for ..."
        // sentence.
        const inlineSelectStyle: Record<
            string,
            (provided: React.CSSProperties) => React.CSSProperties
        > = {
            valueContainer: (provided) => ({
                ...provided,
                height: "20px",
                paddingInline: "0px",
                display: "flex",
                alignItems: "start",
                overflow: "visible",
                top: "-2px",
            }),
            indicatorsContainer: (provided) => ({
                ...provided,
                height: "16px",
                width: "16px",
                justifyContent: "center",
            }),
            container: (provided) => ({
                ...provided,
                border: "none",
                borderBottom: "1px solid",
                borderRadius: "0px",
                margin: "0",
                padding: "0px",
                minWidth: "164px",
                height: "20px",
                top: "2px",
                color: theme.palette.gray[600],
            }),
            singleValue: (provided) => ({
                ...provided,
                transform: "translateY(0%)",
                top: "0%",
                fontSize: "14px",
                fontWeight: 600,
                color: theme.palette.gray[600],
                margin: "0px",
            }),
        };

        // Only models with data on the current chart are offered. Non-analyzable (Lucene/RBC) members
        // that do have data still appear but are disabled with their own tooltip.
        const modelOptions = selectableOptions.map((c) => ({
            label: c.name,
            value: c.id,
            analyzable: c.analyzable,
        }));

        const analyzeSelector = (
            <Grid
                container
                item
                direction="row"
                alignItems="flex-start"
                columnGap={1}
                flexWrap="nowrap"
            >
                <Grid item>
                    <Typography sx={inlineSelectLabelSx}>Analyze</Typography>
                </Grid>
                <Grid item>
                    <AcxSelectSingle
                        id="analyze-with-kbo-select-result"
                        options={resultOptions}
                        valueField="value"
                        labelField="label"
                        defaultValue={resultOptions.find(
                            (o) => o.value === selectedResult,
                        )}
                        onChange={(e) => {
                            setSelectedResult(e.value);
                            refreshKboSummary(e.value);
                        }}
                        isDisabled={isLoading}
                        isSearchable={false}
                        customStyle={inlineSelectStyle}
                    />
                </Grid>
                {modelLoader && (
                    <React.Fragment>
                        <Grid item>
                            <Typography sx={inlineSelectLabelSx}>for</Typography>
                        </Grid>
                        <Grid item>
                            <AcxSelectSingle
                                id="analyze-with-kbo-select-model"
                                options={modelOptions}
                                valueField="value"
                                labelField="label"
                                isOptionDisabled={(o) => !o.analyzable}
                                disabledOptionTooltipText="classifiers do not have reasoning to analyze"
                                defaultValue={modelOptions.find(
                                    (o) => o.value === activeClassifierId,
                                )}
                                onChange={(e) => {
                                    // The active classifier change resets the result to "All Results"
                                    // and re-runs the summary via the refresh effect.
                                    setSelectedClassifierId(e.value);
                                }}
                                isDisabled={isLoading}
                                isSearchable={false}
                                customStyle={inlineSelectStyle}
                            />
                        </Grid>
                    </React.Fragment>
                )}
            </Grid>
        );

        return (
            <AcxGradientBorderContainer>
                {/* This grid ensures the loading indicator is centered  */}
                <Grid container item direction="column" gap={1}>
                    {headerComponent}
                    <Collapse in={isExpanded} unmountOnExit>
                        {analyzeSelector}
                        {hasSummaryToShow && (
                            <Grid item mt={1}>
                                <Markdown>{aiSummaryStore.summary}</Markdown>
                            </Grid>
                        )}
                        {isGoDeeperVisible && (
                            <Grid item mt={1}>
                                <AcxButton
                                    id="analyze-with-kbo-go-deeper"
                                    color="primary"
                                    variant="outlined"
                                    endIcon={<ArrowForwardRounded />}
                                    sx={{
                                        border: "none",
                                        "&:hover": { border: "none" },
                                        "&:disabled": { border: "none" },
                                        px: 1,
                                        py: 0.5,
                                        m: 0,
                                    }}
                                    onClick={onGoDeeperClick}
                                    disabled={!canGoDeeper}
                                >
                                    Go Deeper
                                </AcxButton>
                            </Grid>
                        )}
                        {isLoading && (
                            <Grid item mt={1}>
                                <AcxLoadingIndicator
                                    alternate="PuffLoader"
                                    size={32}
                                    style={{
                                        zIndex: theme.zIndex.appBar - 2,
                                    }}
                                />
                            </Grid>
                        )}
                    </Collapse>
                </Grid>
            </AcxGradientBorderContainer>
        );
    },
);
